import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import { CartProvider } from "@/context/CartContext";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Product from "@/components/Product";
import TheBack from "@/components/TheBack";
import LookCloser from "@/components/LookCloser";
import People from "@/components/People";
import Reviews from "@/components/Reviews";
import Vinyl from "@/components/Vinyl";
import WhoIsItFor from "@/components/WhoIsItFor";
import Packaging from "@/components/Packaging";
import SizeGuide from "@/components/SizeGuide";
import ProductDetails from "@/components/ProductDetails";
import FinalCTA from "@/components/FinalCTA";
import ShutupList from "@/components/ShutupList";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CheckoutModal from "@/components/CheckoutModal";
import StickyBuyBar from "@/components/StickyBuyBar";
import CustomCursor from "@/components/CustomCursor";
import PosterMaker from "@/components/PosterMaker";
import WhatsAppBubble from "@/components/WhatsAppBubble";
import ShopPage from "@/pages/ShopPage";
import AdminPage from "@/pages/AdminPage";
import { SOUND_MAP } from "@/utils/sounds";
import { track } from "@/utils/ga";
import { AuthProvider } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const sessionId = () => {
  let sid = sessionStorage.getItem("sv_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem("sv_sid", sid);
  }
  return sid;
};

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

function LandingPage() {
  return (
    <motion.main
      data-testid="landing-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
    >
      <Hero />
      <Marquee />
      <Product />
      <TheBack />
      <LookCloser />
      <People />
      <Reviews />
      <Vinyl />
      <WhoIsItFor />
      <PosterMaker />
      <Packaging />
      <SizeGuide />
      <ProductDetails />
      <ShutupList />
      <FinalCTA />
    </motion.main>
  );
}

function AnimatedRoutes() {
  const location = useLocation();

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    track("page_view", { page_path: location.pathname, page_title: document.title });
    const ref = sessionStorage.getItem("sv_ref_sent") ? "" : document.referrer || "";
    sessionStorage.setItem("sv_ref_sent", "1");
    fetch(`${API}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sid: sessionId(), path: location.pathname, ref }),
    }).catch(() => {});
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/product" element={<ShopPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const soundHandler = (e) => {
      const t = e.target.closest("[data-sound]");
      if (t) SOUND_MAP[t.dataset.sound]?.();
    };
    window.addEventListener("click", soundHandler);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
      window.removeEventListener("click", soundHandler);
    };
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
      <BrowserRouter>
      <div className="bg-black min-h-screen">
        <Nav />
        <AnimatedRoutes />
        <Footer />
        <CartDrawer />
        <CheckoutModal />
        <StickyBuyBar />
        <WhatsAppBubble />
        <CustomCursor />
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-[100] opacity-[0.05] mix-blend-overlay"
          style={{ backgroundImage: GRAIN }}
        />
      </div>
      </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
